/**
 * Workspace controller mutations — every shape change goes through a serial
 * queue. The optimistic update is applied to the in-memory store at enqueue
 * time so the UI feels instant. The server call, broadcast, and IDB write
 * happen *inside* the queue task — broadcast and IDB are wired to the
 * task's onSuccess so they only fire after the server commits. On failure
 * the controller is marked stale and the user is prompted to refresh.
 *
 * Phase-discriminated guarantee: the private `enqueueOnPersistenceQueue`
 * helper takes `LoadedPhase`, not `WorkspacePhase`. Any caller has to
 * narrow first — deleting the "Stale → return" branch in `runMutation`
 * stops compiling. Compare with the previous flat-state code where
 * deleting `if (state.isStale) return` was a silent regression.
 *
 * Modeled on the old `layoutSyncMiddleware`'s SyncMiddlewareAsyncQueue
 * pattern (see docs/WORKSPACE_UNIFICATION.md).
 */

import { trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS } from '@/services/analytics/events'
import { ConflictError, type MutateTreeOp, type MutateTreeResult } from '../types'
import * as workspaceApi from '../workspace-api'
import { setWorkspaceMeta } from '../idb-cache'
import { isRevisionedBatch } from '../op-policy'
import { isOfflineError } from '../offline-detect'
import type { TreeItems } from '../tree-state-manager'
import { cloneTree } from '../tree-state-manager'
import type { WorkspaceStore } from './store'
import type { BroadcastBinding } from './broadcast'
import type { ErrorPolicy, SerialQueue } from '../serial-queue'
import type { WorkspacePhase } from './state'
import { isSessionEnding } from '@/services/auth/session-ending'

/**
 * Any phase that permits server-bound work. Excludes Stale by
 * construction — `enqueueOnPersistenceQueue` requires this type, so the
 * Stale-narrowing branch in `runMutation` is what allows the call to
 * compile. Deleting that branch would leave `phase: WorkspacePhase` at
 * the call site, which would fail to satisfy this type.
 */
type NonStalePhase = Exclude<WorkspacePhase, { kind: 'Stale' }>

/**
 * Maximum number of mutations we'll queue while offline. Past this
 * threshold the outage is unlikely to be transient and continuing to
 * accept new mutations risks a multi-minute reconciliation when the
 * connection returns. We mark the controller stale instead — the user
 * reloads to recover, accepting the loss of unsaved local edits.
 */
const MAX_OFFLINE_QUEUE = 10

/**
 * Dev-only: delay outgoing cross-tab broadcasts by a fixed window. While the
 * timer is running, sibling tabs are unaware of the local change — they can
 * be driven into a divergent state, then watch reconciliation kick in once
 * the broadcast finally lands.
 *
 * Resolves immediately when off (returns false), or after 5s when armed
 * (returns true). Same localStorage key controls both the controller's
 * `workspace:updated` post and the per-layout `layout:updated` post.
 *
 *   localStorage.setItem('edge-dev-broadcast-delay', '1')
 *   localStorage.removeItem('edge-dev-broadcast-delay')
 *
 * Stripped in prod via `import.meta.env.PROD`.
 */
const BROADCAST_DELAY_MS = 5000
async function shouldDelayBroadcast(): Promise<boolean> {
  if (import.meta.env.PROD) return false
  if (typeof localStorage === 'undefined') return false
  if (localStorage.getItem('edge-dev-broadcast-delay') !== '1') return false
  await new Promise((r) => setTimeout(r, BROADCAST_DELAY_MS))
  return true
}

function bcastLog(...args: unknown[]): void {
  if (import.meta.env.PROD) return
  if (import.meta.env.MODE === 'test') return
  if (typeof localStorage !== 'undefined' && localStorage.getItem('edge-debug-broadcast') === '0')
    return

  console.log('[bcast:ws]', ...args)
}

export interface MutationContext {
  scope: string
  store: WorkspaceStore
  /** Channel for cross-tab notifications. Posts only happen in onSuccess. */
  broadcast?: BroadcastBinding | null
  /** FIFO queue that serializes server-bound mutations. */
  persistenceQueue: SerialQueue
}

export interface OptimisticResult {
  items: TreeItems
  activeLayoutId?: string | null
  openState?: string[]
  createdIds?: string[]
}

export type OptimisticFn = (items: TreeItems) => OptimisticResult

/** Result the queue task hands to onSuccess. */
interface MutationOutcome {
  serverResult: MutateTreeResult
  revisioned: boolean
}

/**
 * Apply an optimistic update locally, then enqueue the server call. The
 * server response is awaited inside the queue so subsequent mutations see
 * the updated revision.
 *
 * The optimistic state always applies — even on a stale controller. A stale
 * controller can't reach the server, but blocking the optimistic apply
 * silently swallows the user's interaction. Instead we apply the change
 * in-memory and re-show the "out of sync" toast so the user understands
 * why nothing's persisting. They reload to recover.
 *
 * If the server call fails the queue's onError fires and marks the
 * controller stale; the optimistic state stays in the in-memory store
 * until the user reloads.
 *
 * `opsBuilder` allows the caller to defer ops construction until after the
 * optimistic phase has produced a client-generated ID (used for create /
 * duplicate).
 */
export function runMutation(
  ctx: MutationContext,
  ops: MutateTreeOp[],
  optimistic: OptimisticFn,
  opsBuilder?: (createdId: string) => MutateTreeOp[],
  /**
   * Fires once the mutation reaches a FINAL outcome: server commit,
   * unrecoverable failure (drop), or never-enqueued (stale controller /
   * queue overflow). NOT fired on offline retry, where the task stays
   * queued and no final outcome exists yet. Used by create/duplicate to
   * settle their pending-create registration (see pending-creates.ts).
   */
  onSettled?: () => void,
): { createdId: string | null } {
  const startState = ctx.store.getState()
  const result = optimistic(cloneTree(startState.data.treeItems))

  // Apply optimistic state to the in-memory store. NOT to IDB — IDB is
  // only written after the server commits. Optimistic apply happens
  // unconditionally — even on Stale, so the UI feels responsive.
  ctx.store.setState((s) => ({
    data: {
      ...s.data,
      treeItems: result.items,
      activeLayoutId:
        result.activeLayoutId !== undefined ? result.activeLayoutId : s.data.activeLayoutId,
      treeOpenState: result.openState !== undefined ? result.openState : s.data.treeOpenState,
      newItemIds: result.createdIds?.length
        ? new Set([...s.data.newItemIds, ...result.createdIds])
        : s.data.newItemIds,
    },
  }))

  // Phase-gated everything below this line.
  const phase = ctx.store.getState().phase

  if (phase.kind === 'Stale') {
    // Inside this branch, `phase.message` is `string`, NOT `string | null`.
    // No fallback chain, no fallback constant.
    showToast(phase.message)
    onSettled?.()
    return { createdId: result.createdIds?.[0] ?? null }
  }

  // ↓ phase is narrowed to NonStalePhase (Loaded or Loading) here.

  // Cap the offline backlog. Past this many queued mutations the outage
  // is unlikely to be transient; mark stale so the user reloads instead
  // of building an unbounded queue.
  if (ctx.persistenceQueue.size() >= MAX_OFFLINE_QUEUE) {
    console.error(
      '[workspace-controller] mutation queue exceeded MAX_OFFLINE_QUEUE; marking stale',
      { size: ctx.persistenceQueue.size() },
    )
    trackEvent(EVENTS.WORKSPACE_MUTATION_QUEUE_OVERFLOW, {
      workspace_id: ctx.scope,
      queue_size: ctx.persistenceQueue.size(),
    })
    markStale(ctx.store)
    onSettled?.()
    return { createdId: result.createdIds?.[0] ?? null }
  }

  const finalOps = opsBuilder && result.createdIds?.length ? opsBuilder(result.createdIds[0]) : ops

  enqueueOnPersistenceQueue(ctx, phase, finalOps, onSettled)

  return { createdId: result.createdIds?.[0] ?? null }
}

/**
 * Push the server-bound task onto the persistence queue. PRIVATE to this
 * module. The `phase: LoadedPhase` parameter encodes the compile-time
 * guarantee — any caller has to narrow phase first. If a future
 * contributor deletes the `if (phase.kind === 'Stale') return` branch in
 * `runMutation`, the call here stops compiling because `phase` would still
 * be `WorkspacePhase`.
 */
function enqueueOnPersistenceQueue(
  ctx: MutationContext,
  _phase: NonStalePhase,
  finalOps: MutateTreeOp[],
  onSettled?: () => void,
): void {
  const revisioned = isRevisionedBatch(finalOps)

  ctx.persistenceQueue.push<MutationOutcome>(
    async () => {
      const serverResult = await workspaceApi.mutateTree(ctx.scope, finalOps, { revisioned })
      return { serverResult, revisioned }
    },
    {
      onSuccess: async (outcome: MutationOutcome) => {
        // Release pending-create waiters first; the IDB/broadcast work
        // below is not something they depend on.
        onSettled?.()
        const { serverResult, revisioned } = outcome
        // Update the in-memory phase. We transition to Loaded with the
        // server-confirmed revision. (For non-revisioned batches, the
        // server returns the same revision it had — still safe to write.)
        ctx.store.setState({
          phase: { kind: 'Loaded', workspaceRevision: serverResult.workspaceRevision },
        })

        // Persist meta to IDB — server-confirmed state.
        const s = ctx.store.getState()
        await setWorkspaceMeta(ctx.scope, {
          version: 2,
          workspaceRevision: serverResult.workspaceRevision,
          activeLayoutId: s.data.activeLayoutId,
          treeItems: s.data.treeItems,
          treeOpenState: s.data.treeOpenState,
        })

        // Broadcast to other tabs — only when the change is revisioned.
        if (!revisioned) {
          bcastLog('mutation onSuccess SKIP post — non-revisioned ops', {
            scope: ctx.scope,
            ops: finalOps.map((o) => o.op),
          })
        } else if (!ctx.broadcast) {
          bcastLog('mutation onSuccess SKIP post — no broadcast binding', {
            scope: ctx.scope,
          })
        } else {
          const delayed = await shouldDelayBroadcast()
          bcastLog(
            delayed ? 'mutation onSuccess → post (after delay)' : 'mutation onSuccess → post',
            {
              scope: ctx.scope,
              workspaceRevision: serverResult.workspaceRevision,
            },
          )
          ctx.broadcast.post({
            type: 'workspace:updated',
            workspaceRevision: serverResult.workspaceRevision,
          })
        }
      },
      onError: (err: unknown): ErrorPolicy => {
        if (isOfflineError(err)) {
          // Transient network failure — keep the task at the head of the
          // queue. The next user action retriggers the queue and re-runs
          // it (which usually succeeds once the network is back).
          console.warn('[workspace-controller] mutation failed (offline); queued for retry', err)
          notifyOffline()
          return 'retry'
        }
        // Conflict (409) or any other server error — unrecoverable
        // without user intervention. Mark stale, drop everything queued.
        console.error('[workspace-controller] mutation failed; marking stale', err)
        markStale(ctx.store, err)
        onSettled?.()
        return 'drop'
      },
    },
  )
}

const STALE_CONFLICT_MESSAGE = 'Your workspace is out of sync. Refresh the page to continue saving.'
const STALE_GENERIC_MESSAGE =
  'There was an issue saving your layout. Please refresh, or try again later.'
const OFFLINE_MESSAGE =
  "You're offline. Your changes are queued and will save when the connection returns."

/** Pick the user-facing message for a save failure. Conflicts get the
 * "out of sync" wording; everything else (4xx/5xx, queue overflow, etc)
 * gets the generic "issue saving... try again later" wording. Offline
 * is handled separately and never reaches markStale. */
function pickStaleMessage(err: unknown): string {
  if (err instanceof ConflictError) return STALE_CONFLICT_MESSAGE
  return STALE_GENERIC_MESSAGE
}

/** Lazy-imports the notification store so unit tests that don't wire one
 * up don't fail. Re-showing replaces any current toast — the desired UX
 * is a fresh reminder on every attempted edit. */
function showToast(message: string, variant: 'error' | 'warning' = 'error'): void {
  void import('@/stores/notification-store').then(({ useNotificationStore }) => {
    if (variant === 'warning') useNotificationStore.getState().showWarning(message)
    else useNotificationStore.getState().showError(message)
  })
}

function notifyOffline(): void {
  showToast(OFFLINE_MESSAGE, 'warning')
}

/**
 * Mark the controller stale. The phase carries the message — re-shows on
 * subsequent edit attempts read `phase.message` directly. After this,
 * runMutation skips the server call but still applies optimistic state.
 */
function markStale(store: WorkspaceStore, err?: unknown): void {
  if (store.getState().phase.kind === 'Stale') return
  // A forced sign-out (e.g. signed in on another device) fails in-flight saves; the
  // reason is on its way to /login, and a save-error toast would bury it.
  if (isSessionEnding()) return
  const message = pickStaleMessage(err)
  store.setState({ phase: { kind: 'Stale', message } })
  showToast(message)
}
